import { useEffect, useMemo, useState } from "react";
import { CARD_TYPES, GRID_SIZE } from "./constants/editor";
import { spriteToDraft } from "./data/sprites";
import { downloadCardJpeg } from "./lib/card-export";
import { getNextSceneScale, getSceneLayer, getSuggestedSceneLayer } from "./lib/card-scene";
import { usePixelEditor } from "./hooks/usePixelEditor";
import { useRoomConnection } from "./hooks/useRoomConnection";
import { findCardType, getFrameColor } from "./lib/card";
import { createDefaultCardDraft, normalizeCardDraft, type CardDraft, type CardType, type FrameStyleId, type FrameWeightId, type SceneLayerSlot } from "./shared/game";
import { Panel } from "./components/common/Panel";
import { BattleArena } from "./components/battle/BattleArena";
import { CardDesigner } from "./components/card/CardDesigner";
import { StaticCard } from "./components/card/StaticCard";
import { FrameControls } from "./components/editor/FrameControls";
import { PalettePicker } from "./components/editor/PalettePicker";
import { SceneLayerControls, SceneLayerPlacementControls } from "./components/editor/SceneLayerControls";
import { SpritePicker } from "./components/editor/SpritePicker";
import { ToolPicker } from "./components/editor/ToolPicker";
import { TypePicker } from "./components/editor/TypePicker";
import { HomeScreen } from "./components/home/HomeScreen";
import { LayoutLab } from "./components/lab/LayoutLab";
import { RoomPanel } from "./components/room/RoomPanel";

export function App() {
  const isLayoutLab = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("layoutLab") === "1";
  const editor = usePixelEditor();
  const room = useRoomConnection();
  const {
    playerId,
    playerName,
    setPlayerName,
    roomCodeInput,
    setRoomCodeInput,
    room: activeRoom,
    connectionStatus,
    error,
    createRoom,
    joinRoom,
    syncCard,
    setReady,
    setRoundDuration,
    setRoundMode,
    restartMatch,
    leaveRoom,
  } = room;

  const [cardName, setCardName] = useState("My Monster");
  const [description, setDescription] = useState("");
  const [hp, setHp] = useState(60);
  const [typeLabel, setTypeLabel] = useState<CardType["label"]>(CARD_TYPES[0].label);
  const [frameStyle, setFrameStyle] = useState<FrameStyleId>("plain");
  const [frameWeight, setFrameWeight] = useState<FrameWeightId>("medium");
  const [frameColorManual, setFrameColorManual] = useState<string | null>(null);
  const [sceneLayers, setSceneLayers] = useState(() => createDefaultCardDraft().sceneLayers);
  const [activeLayerSlot, setActiveLayerSlot] = useState<SceneLayerSlot>("character");

  const currentCard = useMemo<CardDraft>(() => ({
    name: cardName,
    description,
    hp,
    typeLabel,
    frameStyle,
    frameWeight,
    frameColorManual,
    sceneLayers,
    grid: editor.grid,
  }), [cardName, description, hp, typeLabel, frameStyle, frameWeight, frameColorManual, sceneLayers, editor.grid]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      syncCard(currentCard);
    }, 100);

    return () => window.clearTimeout(timeout);
  }, [currentCard, syncCard]);

  const activeType = findCardType(typeLabel);
  const currentPlayer = activeRoom?.players.find((player) => player.id === playerId) ?? null;
  const phase = activeRoom?.phase ?? "lobby";
  const canEditCard = !activeRoom || phase === "drafting" || (phase === "lobby" && activeRoom.round.mode === "solo" && activeRoom.players.length === 1);
  const submissionStatus = currentPlayer?.submission?.status ?? "editing";
  const inBattleView = phase === "battle" || phase === "results";
  const activeSceneLayer = getSceneLayer(currentCard, activeLayerSlot);
  const selectedSpriteId = activeSceneLayer.spriteId;
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const phaseRemainingMs = activeRoom?.round.phaseEndsAt
    ? Math.max(0, Date.parse(activeRoom.round.phaseEndsAt) - nowMs)
    : null;

  useEffect(() => {
    if (!currentPlayer || phase !== "lobby" || activeRoom?.round.mode === "solo") {
      return;
    }

    const nextCard = normalizeCardDraft(currentPlayer.card);
    setCardName(nextCard.name);
    setDescription(nextCard.description);
    setHp(nextCard.hp);
    setTypeLabel(nextCard.typeLabel);
    setFrameStyle(nextCard.frameStyle);
    setFrameWeight(nextCard.frameWeight);
    setFrameColorManual(nextCard.frameColorManual);
    setSceneLayers(nextCard.sceneLayers);
    editor.setGrid(nextCard.grid);
  }, [activeRoom?.round.mode, currentPlayer, editor, phase]);

  const resetCard = () => {
    const emptyCard = normalizeCardDraft(createDefaultCardDraft());
    setCardName(emptyCard.name);
    setDescription(emptyCard.description);
    setHp(emptyCard.hp);
    setTypeLabel(emptyCard.typeLabel);
    setFrameStyle(emptyCard.frameStyle);
    setFrameWeight(emptyCard.frameWeight);
    setFrameColorManual(emptyCard.frameColorManual);
    setSceneLayers(emptyCard.sceneLayers);
    setActiveLayerSlot("character");
    editor.clear();
  };

  const loadSprite = (spriteId: string) => {
    if (activeLayerSlot === "character") {
      const preset = normalizeCardDraft(spriteToDraft(spriteId));
      setCardName(preset.name);
      setDescription(preset.description);
      setTypeLabel(preset.typeLabel);
    }

    setSceneLayers((previous) => previous.map((layer) => (
      layer.slot === activeLayerSlot ? getSuggestedSceneLayer(activeLayerSlot, spriteId) : layer
    )));
  };

  const clearActiveLayer = () => {
    setSceneLayers((previous) => previous.map((layer) => (
      layer.slot === activeLayerSlot ? getSuggestedSceneLayer(activeLayerSlot, null) : layer
    )));
  };

  const moveSceneLayer = (slot: SceneLayerSlot, dx: number, dy: number) => {
    setSceneLayers((previous) => previous.map((layer) => (
      layer.slot === slot
        ? { ...layer, offsetX: layer.offsetX + dx, offsetY: layer.offsetY + dy }
        : layer
    )));
  };

  const scaleSceneLayer = (slot: SceneLayerSlot, delta: number) => {
    setSceneLayers((previous) => previous.map((layer) => (
      layer.slot === slot
        ? { ...layer, scale: getNextSceneScale(layer.scale, delta) }
        : layer
    )));
  };

  const editorCanvasSize = GRID_SIZE * 12;

  if (isLayoutLab) {
    return <LayoutLab />;
  }

  if (!activeRoom) {
    return (
      <HomeScreen
        playerName={playerName}
        setPlayerName={setPlayerName}
        roomCodeInput={roomCodeInput}
        setRoomCodeInput={setRoomCodeInput}
        connectionStatus={connectionStatus}
        error={error}
        onCreateRoom={createRoom}
        onJoinRoom={joinRoom}
      />
    );
  }

  if (inBattleView && activeRoom.round.battle) {
    return (
      <BattleArena room={activeRoom} playerId={playerId} phaseRemainingMs={phaseRemainingMs} onSetReady={setReady} />
    );
  }

  return (
    <main className="page-shell">
      <h1 className="page-title" style={{ color: activeType.color, textShadow: `0 0 20px ${activeType.color}` }}>
        BGJ Card Rooms
      </h1>

      <RoomPanel
        playerId={playerId}
        room={activeRoom}
        connectionStatus={connectionStatus}
        error={error}
        phaseRemainingMs={phaseRemainingMs}
        onExportCard={() => {
          void downloadCardJpeg(currentCard);
        }}
        onSetReady={setReady}
        onSetRoundDuration={setRoundDuration}
        onSetRoundMode={setRoundMode}
        onRestartMatch={restartMatch}
        onLeaveRoom={leaveRoom}
      />

      <div className="layout-grid card-first-layout">
        {!inBattleView && (
          <aside className="stack sidebar-column">
            <Panel title="Room Roster" accent="#6ac5ff">
              <div style={{ display: "grid", gap: 10 }}>
                {activeRoom.players.map((player) => {
                  const accent = getFrameColor(player.card);
                  return (
                    <div key={player.id} style={{ borderRadius: 12, border: `1px solid ${accent}44`, background: "rgba(255,255,255,0.02)", padding: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                        <strong style={{ fontSize: 13 }}>{player.name}{player.id === playerId ? " (you)" : ""}</strong>
                        <span style={{ fontSize: 12, color: player.connected ? "#9df0b4" : "#c7879a" }}>
                          {phase === "lobby" || phase === "countdown"
                            ? (player.isReady ? "ready" : "waiting")
                            : (player.submission?.status ?? "editing")}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#9ea3b5" }}>
                        {player.card.name} · HP {player.card.hp} · Score {player.score}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>

            <Panel title="Sprite Library" accent="#99a3ff">
              <SceneLayerControls
                card={currentCard}
                activeSlot={activeLayerSlot}
                onSelectSlot={setActiveLayerSlot}
                onMoveLayer={moveSceneLayer}
                onScaleLayer={scaleSceneLayer}
                onClearLayer={(slot) => {
                  if (slot === activeLayerSlot) {
                    clearActiveLayer();
                    return;
                  }
                  setSceneLayers((previous) => previous.map((layer) => (
                    layer.slot === slot ? getSuggestedSceneLayer(slot, null) : layer
                  )));
                }}
                disabled={!canEditCard}
              />
              <SpritePicker activeSlot={activeLayerSlot} selectedSpriteId={selectedSpriteId} onLoadSprite={loadSprite} onClear={clearActiveLayer} />
              <SceneLayerPlacementControls
                card={currentCard}
                activeSlot={activeLayerSlot}
                onMoveLayer={moveSceneLayer}
                onScaleLayer={scaleSceneLayer}
                onClearLayer={clearActiveLayer}
                disabled={!canEditCard}
              />
            </Panel>

            <Panel title="Design Controls" accent={activeType.color}>
              <div className="control-stack">
                <div className="control-section">
                  <div className="control-label-row">
                    <span className="control-label">Brush</span>
                    <span className="control-helper">Paint and cleanup</span>
                  </div>
                  <ToolPicker activeTool={editor.tool} onSelect={editor.setTool} brushSize={editor.brushSize} onBrushSizeChange={editor.setBrushSize} />
                  <div className="compact-action-row">
                    <button type="button" onClick={editor.undo} disabled={!editor.canUndo} className="ghost-button" style={{ flex: 1 }}>
                      Undo
                    </button>
                    <button type="button" onClick={editor.redo} disabled={!editor.canRedo} className="ghost-button" style={{ flex: 1 }}>
                      Redo
                    </button>
                    <button type="button" onClick={resetCard} className="active-button" style={{ flex: 1 }}>
                      Clear
                    </button>
                  </div>
                </div>

                <div className="control-section">
                  <div className="control-label-row">
                    <span className="control-label">Card Setup</span>
                    <span className="control-helper">Type, HP, palette, border</span>
                  </div>

                  <div className="builder-grid">
                    <div className="builder-card">
                      <div className="builder-card-header">
                        <span className="control-label">Type</span>
                        <span className="control-helper">Accent driver</span>
                      </div>
                      <TypePicker typeLabel={typeLabel} onSelect={setTypeLabel} disabled={!canEditCard} />
                    </div>

                    <div className="builder-card">
                      <div className="builder-card-header">
                        <span className="control-label">Health</span>
                        <span className="control-helper" style={{ color: activeType.color, fontWeight: 700 }}>HP {hp}</span>
                      </div>
                      <input disabled={!canEditCard} type="range" min="10" max="200" step="10" value={hp} onChange={(event) => setHp(Number(event.target.value))} />
                    </div>
                  </div>

                  <div className="builder-card">
                    <div className="builder-card-header">
                      <span className="control-label">Palette</span>
                      <span className="control-helper">{editor.color.toUpperCase()}</span>
                    </div>
                    <PalettePicker color={editor.color} onSelect={editor.setColor} disabled={!canEditCard} />
                  </div>

                  <div className="builder-card">
                    <div className="builder-card-header">
                      <span className="control-label">Border</span>
                      <span className="control-helper">Style, color, weight</span>
                    </div>
                    <FrameControls
                      frameStyle={frameStyle}
                      setFrameStyle={setFrameStyle}
                      frameWeight={frameWeight}
                      setFrameWeight={setFrameWeight}
                      autoColor={activeType.color}
                      frameColorManual={frameColorManual}
                      setFrameColorManual={setFrameColorManual}
                      disabled={!canEditCard}
                    />
                  </div>
                </div>
              </div>
            </Panel>
          </aside>
        )}

        <section className="editor-surface workspace-column">
          <div className="workspace-grid">
            {inBattleView && activeRoom.round.battle ? (
              <Panel title={phase === "battle" ? "Battle Arena" : "Battle Results"} accent="#ffd166" className="editor-panel">
                <div style={{ marginBottom: 14, fontSize: 13, color: "#d9deea", lineHeight: 1.6 }}>
                  {activeRoom.round.battle.summary}
                </div>
                <div className="battle-card-grid">
                  {activeRoom.round.battle.entries.map((entry) => {
                    const player = activeRoom.players.find((candidate) => candidate.id === entry.playerId);
                    if (!player) {
                      return null;
                    }
                    return (
                      <div key={entry.playerId} className="battle-card-slot">
                        <StaticCard card={player.submission?.card ?? player.card} scale={0.72} />
                        <div className="battle-card-meta">
                          <strong>{entry.playerName}</strong>
                          <span>{entry.typeLabel}</span>
                          <span>HP {entry.hpBefore} {"->"} {entry.hpAfter}</span>
                          <span>Dealt {entry.damageDealt} · Took {entry.damageTaken}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Panel>
            ) : (
            <Panel title={phase === "countdown" ? "Battle Starting" : "Card Studio"} accent={activeType.color} className="editor-panel">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, fontSize: 11, color: "#9ea3b5" }}>
                <span>Phase: {phase}</span>
                <span>Submission: {submissionStatus}</span>
              </div>
              <div className="card-stage-main">
                <CardDesigner
                  card={currentCard}
                  canEdit={canEditCard}
                  onNameChange={setCardName}
                  onDescriptionChange={setDescription}
                  onPointerStart={(row, col) => {
                    if (!canEditCard) {
                      return;
                    }
                    editor.beginStroke();
                    editor.applyAt(row, col);
                  }}
                  onPointerMove={(row, col) => {
                    if (!canEditCard) {
                      return;
                    }
                    editor.applyAt(row, col);
                  }}
                  onPointerEnd={() => {
                    if (!canEditCard) {
                      return;
                    }
                    editor.commitStroke();
                  }}
                />
              </div>
            </Panel>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
